import {
  Component,
  input,
  output,
  inject,
  LOCALE_ID,
  signal,
  computed,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';

import { ConfirmationDialog } from '../../../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { SuccessfulNotification } from '../../../../../utils/successful-notification/successful-notification';
import { ProductService } from '../../../../../services/product/product.service';
import { Tables } from '../../../../../shared/ui/shared-tables/tables/tables';
import { Pagination } from '../../../../../shared/ui/shared-tables/pagination/pagination';

export interface ActionInterface {
  id: number;
  titleEn: string;
  titleAr: string;
}

@Component({
  selector: 'app-sub-products-list',
  standalone: true,
  imports: [
    CommonModule,
    ConfirmationDialog,
    SuccessfulNotification,
    Tables,
    Pagination,
  ],
  templateUrl: './sub-products-list.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        max-width: 100%;
        min-width: 0;
      }

      :host ::ng-deep table {
        min-width: 0 !important;
        width: 100%;
        table-layout: auto;
      }

      :host ::ng-deep th {
        padding: 12px 8px !important;
        white-space: normal !important;
        line-height: 1.4;
      }

      :host ::ng-deep td {
        padding-left: 8px !important;
        padding-right: 8px !important;
      }
    `,
  ],
})
export class SubProductsList {
  private locale = inject(LOCALE_ID);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private productService = inject(ProductService);

  // ================= Input & Output Signals =================

  productId = input.required<string | number>();
  data = input.required<any[]>();
  isLoading = input.required<boolean>();
  total = input.required<number>();

  currentPage = input.required<number>();
  pageSize = input.required<number>();

  pageChange = output<{ page: number; size: number }>();

  // ================= Component State =================

  confirmationModal = signal<boolean>(false);

  type = signal<
    'deactivate-sub' | 'activate-sub' | 'delete-sub' | null
  >(null);

  subProductIdToActivate = signal<number | null>(null);

  errorActivation = signal<string>('');

  isSuccessNotification = signal<boolean>(false);

  loadingActivation = signal<boolean>(false);

  localActiveState = signal<Map<number, boolean>>(new Map());

  deletedIds = signal<Set<number>>(new Set());

  // ================= Computed Helpers =================

  isArabic = computed(
    () =>
      typeof this.locale === 'string' &&
      this.locale.startsWith('ar')
  );

  // ================= Pagination =================

  totalPages = computed(() =>
    Math.ceil(this.total() / (this.pageSize() || 25)) || 1
  );

  startItem = computed(() => {
    const size = this.pageSize() || 25;

    return this.total() === 0
      ? 0
      : (this.currentPage() - 1) * size + 1;
  });

  endItem = computed(() => {
    const size = this.pageSize() || 25;
    const end = this.currentPage() * size;

    return end > this.total()
      ? this.total()
      : end;
  });

  getPageNumbers(): number[] {
    const pages: number[] = [];

    for (let i = 1; i <= this.totalPages(); i++) {
      pages.push(i);
    }

    return pages;
  }

  previousPage(): void {
    if (this.currentPage() > 1) {
      this.pageChange.emit({
        page: this.currentPage() - 1,
        size: this.pageSize() || 25,
      });
    }
  }

  nextPage(): void {
    if (this.currentPage() < this.totalPages()) {
      this.pageChange.emit({
        page: this.currentPage() + 1,
        size: this.pageSize() || 25,
      });
    }
  }

  goToPage(page: number): void {
    if (
      page >= 1 &&
      page <= this.totalPages() &&
      page !== this.currentPage()
    ) {
      this.pageChange.emit({
        page,
        size: this.pageSize() || 25,
      });
    }
  }

  onPageSizeChange(newSize: number): void {
    const size = Number(newSize);

    if (!size || size <= 0) {
      return;
    }

    this.pageChange.emit({
      page: 1,
      size,
    });
  }

  onPaginationSizeChange(): void {
    return;
  }

  // ================= Table Headers Configuration =================

  headerItems = computed(() => [
    { key: 'index', label: '#' },

    {
      key: 'subProductTitle',
      label: $localize`:@@subProductsColTitle:Sub-Product Title`,
    },

    {
      key: 'customerPrice',
      label: $localize`:@@subProductsColPeoplePrice:People Price`,
      type: 'currency',
    },

    {
      key: 'sellerInfo',
      label: `${$localize`:@@subProductsColSellerPrice:Seller Price`} ${$localize`:@@subProductsColDiscount:(Disc. %)`}`,
      type: 'priceDiscount',
    },

    {
      key: 'pharmacyInfo',
      label: `${$localize`:@@subProductsColPharmacyPrice:Pharmacy Price`} ${$localize`:@@subProductsColDiscount:(Disc. %)`}`,
      type: 'priceDiscount',
    },

    {
      key: 'qtyInfo',
      label: $localize`:@@subProductsColQuantity:Available / Total Quantity`,
      type: 'quantity',
    },

    {
      key: 'createdOnBy',
      label: $localize`:@@subProductsColCreated:Created On / Created By`,
    },

    {
      key: 'lastModifiedOnBy',
      label: $localize`:@@subProductsColModified:Last Modified On / Modified By`,
    },

    {
      key: 'activationStatus',
      label: $localize`:@@subProductsColStatus:Status`,
      type: 'status',
    },

    {
      key: 'actions',
      label: '',
      type: 'actions',
    },
  ]);

  // ================= Row Actions Configuration =================

  readonly rowActions: ActionInterface[] = [
    {
      id: 1,
      titleEn: 'View Product Details',
      titleAr: 'عرض تفاصيل الصنف الفرعي',
    },
    {
      id: 2,
      titleEn: 'Edit',
      titleAr: 'تعديل الصنف الفرعي',
    },
    {
      id: 3,
      titleEn: 'Delete',
      titleAr: 'حذف الصنف الفرعي',
    },
    {
      id: 4,
      titleEn: 'Mark as inactive',
      titleAr: 'تعيين كغير نشط',
    },
    {
      id: 5,
      titleEn: 'Mark as Active',
      titleAr: 'تعيين كنشط',
    },
  ];

  currentActions(): ActionInterface[] {
    return this.rowActions;
  }

  // ================= Helper Methods =================

  isProductActive(id: number): boolean {
    if (this.localActiveState().has(id)) {
      return this.localActiveState().get(id)!;
    }
    const found = this.data().find((item) => item.id === id);
    return found ? !!found.isActive : false;
  }

  // ================= Table Rows Data Mapping =================

  tableRows = computed(() => {
    const ar = this.isArabic();
    const page = this.currentPage();
    const perPage = this.pageSize() || 25;
    const currency = $localize`:@@subProductsCurrency:EGP`;
    const deleted = this.deletedIds();

    return this.data()
      .filter((item) => !deleted.has(item.id))
      .map((item, i) => {
        const rawTitle = ar ? item.titleAr : item.titleEn;

        const createdByName = item.createdBy?.fullName;

        const lastModifiedByName =
          item.lastModifiedBy?.fullName || createdByName;

        const active = this.isProductActive(item.id);

        return {
          ...item,

          index: i + 1 + (page - 1) * perPage,

          subProductTitle: rawTitle || '-',

          customerPrice: item.customerPrice ?? '-',

          sellerInfo:
            item.sellerPrice != null
              ? {
                  price: `${item.sellerPrice} ${currency}`,
                  discount: `(%${
                    item.sellerPriceDiscountPercentage ?? 0
                  })`,
                }
              : null,

          pharmacyInfo:
            item.businessPrice != null
              ? {
                  price: `${item.businessPrice} ${currency}`,
                  discount: `(%${
                    item.businessPriceDiscountPercentage ?? 0
                  })`,
                }
              : null,

          qtyInfo:
            item.availableQuantity != null && item.totalQuantity != null
              ? {
                  available: item.availableQuantity,
                  total: item.totalQuantity,
                }
              : null,

          createdOn: item.createdOn || null,

          createdBy: createdByName || '-',

          lastModifiedOn:
            item.lastModifiedOn || item.createdOn || null,

          lastModifiedBy: lastModifiedByName || '-',

          activationStatus: active
            ? {
                titleEn: 'Active',
                titleAr: 'مفعل',
              }
            : {
                titleEn: 'Inactive',
                titleAr: 'غير مفعل',
              },
        };
      });
  });

  // ================= Confirmation Modal Handling =================

  confirmationModalContent = computed(() => {
    const currentType = this.type();

    if (currentType === 'deactivate-sub') {
      return {
        title: $localize`:@@subProductsConfirmDeactivation:Confirm Deactivation`,
        description: $localize`:@@subProductsDeactivateDesc:Are you sure you want to deactivate this product?`,
        confirmContent: this.loadingActivation()
          ? $localize`:@@subProductsDeactivating:Deactivating...`
          : $localize`:@@subProductsConfirmDeactivation:Confirm Deactivation`,
      };
    }

    if (currentType === 'activate-sub') {
      return {
        title: $localize`:@@subProductsActivateTitle:Confirm Sub-product Activation`,
        description: $localize`:@@subProductsActivateDesc:Are you sure you want to activate this sub-product? The sub-product will become visible to customers and available for purchase.`,
        confirmContent: this.loadingActivation()
          ? $localize`:@@subProductsActivating:Activating...`
          : $localize`:@@subProductsConfirmActivation:Confirm Activation`,
      };
    }

    if (currentType === 'delete-sub') {
      return {
        title: $localize`:@@subProductsDeleteTitle:Confirm Sub-product Deletion`,
        description: $localize`:@@subProductsDeleteDesc:Are you sure you want to delete this sub-product? This action cannot be undone.`,
        confirmContent: this.loadingActivation()
          ? $localize`:@@subProductsDeleting:Deleting...`
          : $localize`:@@subProductsConfirmDeletion:Confirm Deletion`,
      };
    }

    return null;
  });

  dialogType = computed<'deactivate' | 'activate' | null>(() => {
    const currentType = this.type();
    if (currentType === 'activate-sub') {
      return 'activate';
    }
    if (currentType === 'deactivate-sub' || currentType === 'delete-sub') {
      return 'deactivate';
    }
    return null;
  });

  onHandleAction(event: {
    event?: Event;
    actionId: number;
    entity?: any;
    row?: any;
  }): void {
    const actionId = event.actionId;
    const row = event.entity ?? event.row;

    if (!row) return;

    if (actionId === 1) {
      this.router.navigate([
        '/admin/products/view-sub-product',
        this.productId(),
        row.id,
      ]);
      return;
    }

    if (actionId === 2) {
      this.router.navigate([
        '/admin/products/edit-sub-product',
        this.productId(),
        row.id,
      ]);
      return;
    }
    if (actionId === 3) {
      this.subProductIdToActivate.set(row.id);
      this.type.set('delete-sub');
      this.confirmationModal.set(true);
      return;
    }

    if (actionId === 4) {
      this.subProductIdToActivate.set(row.id);
      this.type.set('deactivate-sub');
      this.confirmationModal.set(true);
      return;
    }

    if (actionId === 5) {
      this.subProductIdToActivate.set(row.id);
      this.type.set('activate-sub');
      this.confirmationModal.set(true);
      return;
    }
  }

  onCancelConfirmationModal(): void {
    this.confirmationModal.set(false);
    this.subProductIdToActivate.set(null);
    this.type.set(null);
    this.errorActivation.set('');
  }

  onConfirm(): void {
    const subId = this.subProductIdToActivate();
    const mainProductId = Number(this.productId());
    const currentType = this.type();

    if (!subId || !mainProductId || !currentType) return;

    this.loadingActivation.set(true);
    this.errorActivation.set('');

    if (currentType === 'deactivate-sub') {
      this.productService
        .deactivateSubProduct(mainProductId, subId)
        .pipe(finalize(() => this.loadingActivation.set(false)))
        .subscribe({
          next: () => {
            this.localActiveState.update((map) => {
              const newMap = new Map(map);
              newMap.set(subId, false);
              return newMap;
            });
            this.showSuccessNotification();
          },
          error: (err) => {
            console.error('Deactivation error:', err);
            this.errorActivation.set(
              $localize`:@@subProductsDeactivateError:An error occurred while deactivating the product. Please try again.`
            );
          },
        });
    } else if (currentType === 'activate-sub') {
      this.productService
        .activateSubProduct(mainProductId, subId)
        .pipe(finalize(() => this.loadingActivation.set(false)))
        .subscribe({
          next: () => {
            this.localActiveState.update((map) => {
              const newMap = new Map(map);
              newMap.set(subId, true);
              return newMap;
            });
            this.showSuccessNotification();
          },
          error: (err) => {
            console.error('Activation error:', err);
            this.errorActivation.set(
              $localize`:@@subProductsActivateError:An error occurred while activating the product. Please try again.`
            );
          },
        });
    } else if (currentType === 'delete-sub') {
      this.productService
        .deleteSubProduct(mainProductId, subId)
        .pipe(finalize(() => this.loadingActivation.set(false)))
        .subscribe({
          next: () => {
            this.deletedIds.update((set) => {
              const newSet = new Set(set);
              newSet.add(subId);
              return newSet;
            });
            this.showSuccessNotification();
          },
          error: (err) => {
            console.error('Deletion error:', err);
            this.errorActivation.set(
              $localize`:@@subProductsDeleteError:An error occurred while deleting the sub-product. Please try again.`
            );
          },
        });
    }
  }

  private showSuccessNotification(): void {
    this.confirmationModal.set(false);
    this.isSuccessNotification.set(true);
    this.cdr.markForCheck();

    setTimeout(() => {
      this.isSuccessNotification.set(false);
      this.subProductIdToActivate.set(null);
      this.type.set(null);
      this.cdr.markForCheck();
    }, 3000);
  }
}